import { differenceInMinutes } from 'date-fns';
import { isFunction } from 'lodash';
import { useRecoilValue } from 'recoil';

import { CarrierLogos } from '@/components/carrier-logos';
import { EmissionsIndicator } from '@/components/modules/emissions/indicator';
import { JourneyRowTemplate } from '@/components/modules/journey/row-template';
import { OptionViaBadge } from '@/components/modules/option/via-badge';
import { RouteBarSequence } from '@/components/route-bar-sequence';
import { Text } from '@/components/text';
import { Tooltip } from '@/components/tooltip';
import { RateBadge } from '@/components/ui/rate-badge';
import { DATE_FORMAT, ENTIRE_AVAILABLE, MODE_WATER, TIME_FORMAT } from '@/lib/constants';
import { getServiceLineNames } from '@/lib/models/lines/utils/get-line-service-ids';
import { SpotRateModel } from '@/lib/models/rates/types';
import { getCarriersByIdsState } from '@/state/carriers';
import { getLinesByIds } from '@/state/lines';
import { getSpotRatesByCarrierIdsState } from '@/state/rates';
import { EmissionStats, RouteScheduleInterface } from '@/types';
import { getCarrierNames } from '@/utils/carrier';
import { formatDuration, formatToLocationDateTime } from '@/utils/format';
import { getScheduleOptionStopsShortString } from '@/utils/strings';
import { tv } from '@/utils/styles';
import { trackUser } from '@/utils/tracking';

import { routeSegmentPanelStyles } from '../shared/styles';

interface Props {
  option: RouteScheduleInterface;
  index: number;
  timeRangeHours: number;
  rangeStartTime: Date;
  shouldShowRates?: boolean;
  emissionStats?: EmissionStats;
  onRowClick?: (option: RouteScheduleInterface, rates?: SpotRateModel[]) => void;
}

const RouteScheduleRow = ({
  option,
  index,
  timeRangeHours,
  rangeStartTime,
  shouldShowRates = false,
  emissionStats,
  onRowClick,
}: Props) => {
  const {
    id,
    mode,
    segments,
    departureDateTime,
    arrivalDateTime,
    duration,
    from,
    to,
    attributes,
    isPredicted,
    emissions,
    lineIds,
    via,
  } = option;

  const lines = useRecoilValue(getLinesByIds(lineIds));

  const isOceanSchedule = mode === MODE_WATER;
  const showTimeFirst = !isOceanSchedule;

  // Get Timezones
  const departureTimezone = from.timezoneName;
  const arrivalTimezone = to.timezoneName;

  // Departure Time
  const departureDateString = formatToLocationDateTime(departureDateTime, departureTimezone, DATE_FORMAT);
  const departureTimeString = formatToLocationDateTime(departureDateTime, departureTimezone, TIME_FORMAT);

  // Arrival Time
  const arrivalDateString = formatToLocationDateTime(arrivalDateTime, arrivalTimezone, DATE_FORMAT);
  const arrivalTimeString = formatToLocationDateTime(arrivalDateTime, arrivalTimezone, TIME_FORMAT);

  const carrierIds = option.attributes.primaryCarriers;

  // Determine how to to draw the bars
  const scheduleDurationInHrs = duration / 60 / 60;
  const schedulePercent = (scheduleDurationInHrs / timeRangeHours) * 100;
  const scheduleOffset = differenceInMinutes(departureDateTime, rangeStartTime);
  const scheduleOffsetPercent = (scheduleOffset / 60 / timeRangeHours) * 100;

  // Get carrier details
  const carriers = useRecoilValue(getCarriersByIdsState(carrierIds));
  const carrierNames = getCarrierNames(carriers);

  // Rates
  const rates = useRecoilValue(getSpotRatesByCarrierIdsState(carrierIds));

  // Generate Bar labels
  const lineNames = lines.length ? lines.map((line) => getServiceLineNames(line, carrierIds)) : undefined;
  const segmentLabels = lineNames || carrierNames;

  // Freight details
  const isFreighter = attributes.hasFreightOnlyVehicles === ENTIRE_AVAILABLE && !isOceanSchedule;
  const hasVia = !!via.length;

  // Handle Click more details
  const handleDetailsClick = (): void => {
    if (isFunction(onRowClick)) onRowClick(option, rates);
    // Analytics Event
    trackUser.event('Schedule Option Details', { mode, option: index + 1 });
  };

  const { rowPrimaryText, rowSubText } = routeSegmentPanelStyles();

  const { grid, logo, gridBlock, barWrapper, bar, feature } = styles({ withPrice: shouldShowRates });

  return (
    <JourneyRowTemplate
      onClickDetails={handleDetailsClick}
      isPredicted={isPredicted}
      actionButtonClassName="md:absolute"
    >
      <div className={grid()}>
        <div className={logo()}>
          <CarrierLogos carriers={carriers} mode={mode} limit={2} />
        </div>
        <div className={gridBlock({ className: 'border-none' })}>
          <div>
            <Text className={rowPrimaryText()}>{showTimeFirst ? departureTimeString : departureDateString}</Text>
            <Text className={rowSubText()}>{showTimeFirst ? departureDateString : departureTimeString}</Text>
          </div>
        </div>
        <div className={gridBlock()}>
          <div>
            <Text className={rowPrimaryText()}>{showTimeFirst ? arrivalTimeString : arrivalDateString}</Text>
            <Text className={rowSubText()}>{showTimeFirst ? arrivalDateString : arrivalTimeString}</Text>
          </div>
        </div>
        <div className={gridBlock()}>
          <div>
            <Text className={rowPrimaryText()}>{formatDuration(duration)}</Text>
            <Text className={rowSubText()}>
              {hasVia ? <OptionViaBadge via={via} isShort /> : getScheduleOptionStopsShortString(option)}
              {isFreighter && (
                <Tooltip content="Freighter">
                  <span className={feature()}>F</span>
                </Tooltip>
              )}
            </Text>
          </div>
        </div>
        <div className={gridBlock({ className: 'pr-4' })}>
          <EmissionsIndicator
            mode={mode}
            value={emissions?.wtw}
            unit={emissions?.unit}
            comparison={emissionStats}
            wtwBreakdown={emissions}
          />
        </div>
        <div className={barWrapper()}>
          <div className={bar()}>
            <div style={{ width: `${scheduleOffsetPercent}%` }} />
            <RouteBarSequence
              id={id}
              mode={mode}
              segments={segments}
              percent={schedulePercent}
              segmentLabels={segmentLabels}
              passThroughContents={false}
              showWarnings
            />
          </div>
        </div>
        {shouldShowRates && (
          <div className={gridBlock({ className: 'relative flex justify-end py-0' })}>
            <RateBadge rates={rates} />
          </div>
        )}
      </div>
    </JourneyRowTemplate>
  );
};

const styles = tv({
  slots: {
    barWrapper: 'hidden items-center md:flex',
    grid: 'relative z-5 grid grid-cols-2 gap-4 pl-10 md:grid-cols-[var(--timeline-spacing)] md:gap-0 md:pl-0',
    logo: 'absolute left-1 top-1 flex w-10 justify-center md:relative md:-left-8 md:top-1 md:w-20',
    gridBlock: 'flex items-center border-l-0 border-l-grey-200 px-4 py-1 pr-0 md:border-l',
    bar: "relative flex h-5 w-full bg-grey-100 before:absolute before:-left-2.5 before:bottom-0 before:top-0 before:w-2.5 before:rounded-l-full before:bg-[inherit] before:content-[''] after:absolute after:-right-2.5 after:bottom-0 after:left-auto after:top-0 after:w-2.5 after:rounded-r-full after:bg-[inherit] after:content-['']",
    feature: 'ml-2 rounded-lg bg-lightBlue-50 px-[5px] py-[2px] text-xs font-medium text-lightBlue-800',
  },
  variants: {
    withPrice: {
      true: {
        grid: 'md:grid-cols-[var(--timeline-spacing-with-price)]',
      },
    },
  },
});

export { RouteScheduleRow };
